/**
 * Sentry crash reporting initialization for the admin web app.
 *
 * Mirrors the privacy stance documented in
 * `docs/compliance/privacy-policy.md` §3.7 and §7.5:
 * - PII is never sent (sendDefaultPii=false)
 * - No session replay, no transactions, errors only
 * - User context is the Firebase UID only (already pseudonymous)
 * - beforeSend strips known PII keys from event payloads as a defense-
 *   in-depth layer in case future code or a third-party library adds them
 *
 * Disabled when DSN is empty (local dev) or when running against the
 * emulator. Production builds inject DSN via Vite environment variables.
 */
import * as Sentry from '@sentry/react';
import { scrubPii } from '@event-planner/shared';

export function initSentry(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  const useEmulators = import.meta.env.VITE_USE_EMULATORS === 'true';

  if (!dsn || useEmulators) {
    return;
  }

  Sentry.init({
    dsn,
    sendDefaultPii: false,
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    attachStacktrace: true,
    initialScope: {
      tags: { app: 'admin' },
    },
    beforeSend(event) {
      if (event.user) {
        event.user = { id: event.user.id };
      }
      if (event.request) {
        event.request = scrubPii(event.request) as typeof event.request;
      }
      if (event.extra) {
        event.extra = scrubPii(event.extra) as typeof event.extra;
      }
      if (event.contexts) {
        event.contexts = scrubPii(event.contexts) as typeof event.contexts;
      }
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.data) {
        breadcrumb.data = scrubPii(breadcrumb.data) as typeof breadcrumb.data;
      }
      return breadcrumb;
    },
  });
}

export { Sentry };
